// Navigation utility to provide React Navigation-like API for screens
import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return {
    navigate: (route, params) => {
      if (typeof route === 'string') {
        // Handle route strings like "Home" or "/home"
        let path = route.startsWith('/') ? route : `/${route.toLowerCase()}`;
        
        // Handle common route mappings
        const routeMap = {
          'home': '/home',
          'profile': '/profile',
          'classdetail': '/class',
          'setdetail': '/set',
          'createset': '/create-set',
          'editset': '/edit-set',
          'createflashcard': '/create-flashcard',
          'editflashcard': '/edit-flashcard',
          'play': '/play',
          'aigenerate': '/ai-generate',
          'documenttoflashcards': '/document-to-flashcards',
          'privacypolicy': '/privacy-policy',
          'termsofservice': '/terms-of-service',
          'helpcenter': '/help-center',
          'about': '/about',
          'settings': '/settings',
          'pricing': '/pricing',
          'login': '/login',
          'register': '/register',
          'initial': '/initial',
          'apponboarding': '/onboarding',
        };

        const normalizedRoute = route.toLowerCase().replace(/\s+/g, '');
        if (routeMap[normalizedRoute]) {
          path = routeMap[normalizedRoute];
        }

        // Handle params
        if (params) {
          // If params is an object with route-specific params
          if (params.classId) {
            if (normalizedRoute === 'createset') {
              // For CreateSet, pass classId via state
              navigate(path, { state: { classId: params.classId } });
              return;
            }
            path = `/class/${params.classId}`;
          } else if (params.setId) {
            if (normalizedRoute === 'play') {
              path = `/play/${params.setId}`;
            } else if (normalizedRoute === 'setdetail') {
              path = `/set/${params.setId}`;
            } else if (normalizedRoute === 'editset') {
              path = `/edit-set/${params.setId}`;
            } else if (normalizedRoute === 'createflashcard') {
              path = `/create-flashcard/${params.setId}`;
            } else if (normalizedRoute === 'aigenerate' || normalizedRoute === 'documenttoflashcards') {
              // For AI Generate and Document to Flashcards, pass setId via state
              navigate(path, { state: { setId: params.setId } });
              return;
            }
          } else if (params.cardId) {
            path = `/edit-flashcard/${params.cardId}`;
          } else if (params.referrerId) {
            // For register screen with referrer
            navigate(path, { state: { referrerId: params.referrerId } });
            return;
          } else if (params.class) {
            // Handle class object
            path = `/class/${params.class.id}`;
          } else if (params.set) {
            // Handle set object
            const setRoute = location.pathname.includes('class') ? 'setdetail' : 'play';
            if (setRoute === 'play') {
              path = `/play/${params.set.id}`;
            } else {
              path = `/set/${params.set.id}`;
            }
          }
        }

        navigate(path);
      }
    },
    goBack: () => {
      navigate(-1);
    },
    replace: (route, params) => {
      if (typeof route === 'string') {
        let path = route.startsWith('/') ? route : `/${route.toLowerCase()}`;
        
        // Handle common route mappings (same as navigate)
        const routeMap = {
          'home': '/home',
          'profile': '/profile',
          'classdetail': '/class',
          'setdetail': '/set',
          'createset': '/create-set',
          'editset': '/edit-set',
          'createflashcard': '/create-flashcard',
          'editflashcard': '/edit-flashcard',
          'play': '/play',
          'aigenerate': '/ai-generate',
          'documenttoflashcards': '/document-to-flashcards',
          'privacypolicy': '/privacy-policy',
          'termsofservice': '/terms-of-service',
          'helpcenter': '/help-center',
          'about': '/about',
          'settings': '/settings',
          'pricing': '/pricing',
          'login': '/login',
          'register': '/register',
          'initial': '/initial',
          'apponboarding': '/onboarding',
        };

        const normalizedRoute = route.toLowerCase().replace(/\s+/g, '');
        if (routeMap[normalizedRoute]) {
          path = routeMap[normalizedRoute];
        }

        // Handle params
        if (params) {
          if (params.setId) {
            if (normalizedRoute === 'setdetail') {
              path = `/set/${params.setId}`;
            }
          }
        }

        navigate(path, { replace: true });
      } else {
        navigate(route, { replace: true });
      }
    },
    setOptions: (options) => {
      // For web, header options are handled differently
      // This is a no-op for now, but can be extended if needed
      console.log('setOptions called:', options);
    },
  };
};

export const useRoute = () => {
  const params = useParams();
  const location = useLocation();

  return {
    params: { ...params, ...(location.state || {}) },
    name: location.pathname,
  };
};

// Hook for focus effect (simplified for web)
export const useFocusEffect = (callback) => {
  const location = useLocation();
  
  React.useEffect(() => {
    callback();
  }, [location.pathname]);
};

